import axios from "axios";
//import { isAfter, isSameDay, formatDate, parseISO, subDays } from 'date-fns';
import SvgIcon from '@mui/material/SvgIcon';
import * as Flags from 'country-flag-icons/react/3x2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';


export const getValueByKey = (optionsList, key, language, type) => {
  if (!optionsList || !key) return "-";

  // Suche den exakten Eintrag, bei dem Key, Sprache und Typ übereinstimmen
  const found = optionsList.find(
    (item) => 
      item.key === key && 
      item.language === language && 
      item.type === type
  );

  // Wenn gefunden, gib den Wert zurück, andernfalls den Key (besser als eine leere Anzeige)
  return found ? found.value : key;
};

/**
 * Wandelt beliebige Datumsstrings (ISO-Z, Deutsch, Bindestriche) sicher in YYYY-MM-DD um.
 * @param {string} dateStr - Das umzuwandelnde Datum
 * @param {string} fallbackDate - Standardwert, falls ungültig (default: "1990-01-01")
 * @returns {string} Formatiertes ISO-Datum für HTML5-Inputs
 */
export const formatToIsoDate = (dateStr, fallbackDate = "1990-01-01") => {
  // 1. Abfangen von leeren Werten, Bindestrichen oder dem Fallback selbst
  if (!dateStr || dateStr.trim() === "" || dateStr === "-" || dateStr.includes(fallbackDate)) {
    return fallbackDate;
  }

  // 2. Fall A: Es ist ein ISO-String aus Go/SQLite (z.B. "1946-01-14T00:00:00Z")
  // Mindestlänge für ein valides ISO-Datum (YYYY-MM-DD) ist 10 Zeichen
  if (dateStr.includes("-") && dateStr.trim().length >= 10) {
    return dateStr.substring(0, 10); // Schneidet exakt "1946-01-14" ab
  }

  // 3. Fall B: Es ist ein deutsches Format (z.B. "14.01.1946")
  if (dateStr.includes(".")) {
    const parts = dateStr.split(".");
    if (parts.length === 3) {
      const day = parts[0].padStart(2, "0");
      const month = parts[1].padStart(2, "0");
      const year = parts[2].trim();
      return `${year}-${month}-${day}`;
    }
  }

  return fallbackDate;
};

/**
 * Liefert ein echtes MUI-konformes SvgIcon für das jeweilige Land
 * @param {string} countryCode - ISO-Code (z.B. "DE", "US")
 * @returns {JSX.Element|null}
 */
export const displayFlagByCountryCode = ( countryCode ) => {
  if (!countryCode || countryCode.length !== 2) return null;

  // Code in Großbuchstaben umwandeln, da die Komponenten "DE", "US" etc. heißen
  const code = countryCode.toUpperCase();
  const FlagComponent = Flags[code];

  if (!FlagComponent) return null;

  return (
  <SvgIcon 
      sx={{ 
        verticalAlign: 'middle', 
        fontSize: '1.25rem', // Passt sich perfekt an Textgröße an
        borderRadius: '2px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        overflow: 'hidden' // Verhindert unsaubere Ecken beim Radius
      }}
    > 
      <FlagComponent />
    </SvgIcon>
  );
};

export const displayBool = (value) => {
  return (
    <Box display="inline-flex" alignItems="center" style={{ verticalAlign: "middle" }}>
      {value ? (
        <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "1.2rem" }} /> // Grüner Haken
      ) : (
        <CancelIcon sx={{ color: "#d32f2f", fontSize: "1.2rem" }} />      // Rotes Kreuz
      )}
    </Box>
  );
};

// 1. Für das Release-Datum (Nur das reine Datum ohne Uhrzeit)
export const displayDateByLanguage = (dateStr, language = 'de') => {
  if (!dateStr) return "";
  const emptyDate1 = "1990-01-01";
  const emptyDate2 = "1990-01-01T00:00:00Z";
  const emptyDate3 = "0001-01-01T00:00:00Z";
  if (dateStr === emptyDate1) return "";
  if (dateStr === emptyDate2) return "";
  if (dateStr === emptyDate3) return "";
  
  // Falls das Datum Leerzeichen statt "T" enthält (GORM/SQLite-Format), machen wir es JS-kompatibel
  const safeDateStr = dateStr.includes(" ") ? dateStr.replace(" ", "T") : dateStr;
  const date = new Date(safeDateStr);
  
  if (isNaN(date.getTime())) return dateStr; // Fallback falls Parsing fehlschlägt

  // Formatiert das Datum anhand der Sprache (z.B. "de" -> DD.MM.YYYY, "en" -> M/D/YYYY)
  return new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

// 2. Für created_at (Vollständiger Zeitstempel mit Datum und Uhrzeit)
export const displayDateTimeByLanguage = (dateStr, language = 'de') => {
  if (!dateStr) return "";
  
  const safeDateStr = dateStr.includes(" ") ? dateStr.replace(" ", "T") : dateStr;
  const date = new Date(safeDateStr);
  
  if (isNaN(date.getTime())) return dateStr;

  // Formatiert Datum UND Uhrzeit sekundengenau
  return new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

/* Split string by 'T' */
export const displayTimeAsString = (str) => {
  // ⭐ 1. Sicherheits-Check: Falls das Datum (noch) undefined oder leer ist, 
  // geben wir einen leeren String zurueck anstatt abzustürzen!
  if (!str) {
    return "";
  }

  // ⭐ 2. Flexibler Split: Teilt das Datum entweder beim "T" (ISO) 
  // oder beim Leerzeichen " " (SQLite/GORM Standard) auf.
  if (str.includes("T")) {
    return str.split("T")[0];
  } else if (str.includes(" ")) {
    return str.split(" ")[0];
  }

  // Fallback: Falls es bereits nur ein reines Datum ist
  return str;
};

/* Returns sheet with sheetName from sheetPages */
export const findSheetByPages = (safeSheetName, sheetPages) => {
  if (!sheetPages) return null;
  for (let page in sheetPages) {
    // ⭐ FIX: Nutzt jetzt safeSheetName (CamelCase) statt safe_sheet_name
    const found = sheetPages[page].find(s => s && s.safeSheetName === safeSheetName);
    if (found) return found;
  }
  return null;
};

/* Returns sheet with sheetName from sheets */
export const findSheetBySheets = (safeSheetName, sheets) => {
  if (!sheets) return null;
  // ⭐ FIX: Nutzt jetzt safeSheetName (CamelCase) statt safe_sheet_name
  return sheets.find(s => s && s.safeSheetName === safeSheetName) || null;
};

/* Returns composer with composerName from composerPages */
export function findComposerByPages(safeComposerName, composerPages) {
  for (let key in composerPages) {
    if (!composerPages.hasOwnProperty(key)) continue;
    const page = composerPages[key];
    const result = page.find(
      (composer) => composer.safe_name === safeComposerName
    );

    if (result !== undefined) return result;
  }
};

/* Returns composer with composerName from composers */
export function findComposerByComposers(safeComposerName, composers) {
  return composers.find((composer) => composer.safe_name === safeComposerName);
};

export function getCompImgUrl(portraitURL) {
  return portraitURL.includes("http")
    ? portraitURL
    : axios.defaults.baseURL + portraitURL;
};

export const formatMsToDuration = (ms) => {
    if (!ms) return "";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };